import Swiper from 'swiper';
import { Navigation, Pagination, Keyboard, A11y, Autoplay } from 'swiper/modules';

const PLAY_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="19" height="20" viewBox="0 0 19 20" fill="none" aria-hidden="true" focusable="false">'
  + '<path d="M17.409 7.35331C17.8893 7.60872 18.291 7.99 18.5712 8.45629C18.8514 8.92259 18.9994 9.45632 18.9994 10.0003C18.9994 10.5443 18.8514 11.078 18.5712 11.5443C18.291 12.0106 17.8893 12.3919 17.409 12.6473L4.597 19.6143C2.534 20.7373 0 19.2773 0 16.9683V3.03331C0 0.723308 2.534 -0.735693 4.597 0.385307L17.409 7.35331Z" fill="currentColor"/>'
  + '</svg>';

const PAUSE_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="19" height="24" viewBox="0 0 19 24" fill="none" aria-hidden="true" focusable="false">'
  + '<path d="M5.09896 0H1.69974C0.761065 0 0.00012207 0.735857 0.00012207 1.64358V21.3666C0.00012207 22.2743 0.761065 23.0102 1.69974 23.0102H5.09896C6.03763 23.0102 6.79857 22.2743 6.79857 21.3666V1.64358C6.79857 0.735857 6.03763 0 5.09896 0Z" fill="currentColor"/>'
  + '<path d="M16.7931 0H13.3938C12.4552 0 11.6942 0.735857 11.6942 1.64358V21.3666C11.6942 22.2743 12.4552 23.0102 13.3938 23.0102H16.7931C17.7317 23.0102 18.4927 22.2743 18.4927 21.3666V1.64358C18.4927 0.735857 17.7317 0 16.7931 0Z" fill="currentColor"/>'
  + '</svg>';

const prefersReducedMotion =
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function wireAutoplayToggle(swiper, el) {
  const toggle = el.querySelector('.js-carousel-toggle');
  if (!toggle) {
    return;
  }

  let paused = prefersReducedMotion;

  function render() {
    toggle.innerHTML = paused ? PLAY_SVG : PAUSE_SVG;
    toggle.setAttribute('aria-pressed', paused ? 'false' : 'true');
    toggle.setAttribute('aria-label', paused ? 'Play carousel' : 'Pause carousel');
  }

  if (paused) {
    swiper.autoplay.stop();
  }
  render();

  toggle.addEventListener('click', () => {
    paused = !paused;
    if (paused) {
      swiper.autoplay.stop();
    } else {
      swiper.autoplay.start();
    }
    render();
  });
}

// Sit the toggle in the bottom-right corner of the active slide's image. Image
// dimensions vary per slide, so this is measured in JS and re-run whenever the
// slide, size, or image load state changes.
function alignToggleToImage(swiper, el, imageSelector) {
  const toggle = el.querySelector('.js-carousel-toggle');
  if (!toggle) {
    return;
  }

  function reposition() {
    const slide = swiper.slides[swiper.activeIndex];
    const img = slide && slide.querySelector(imageSelector);
    if (!img) {
      return;
    }

    const swiperRect = el.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    if (!imgRect.height) {
      img.addEventListener('load', reposition, { once: true });
      return;
    }

    toggle.style.left = 'auto';
    toggle.style.right = (swiperRect.right - imgRect.right + 16) + 'px';
    toggle.style.bottom = (swiperRect.bottom - imgRect.bottom + 11) + 'px';
  }

  reposition();
  swiper.on('slideChange', reposition);
  swiper.on('slideChangeTransitionEnd', reposition);
  swiper.on('resize', reposition);
  swiper.on('imagesReady', reposition);
  window.addEventListener('load', reposition);
  window.addEventListener('resize', reposition);
}

// Always an object (never `false`) so swiper.autoplay.start() works after a
// reduced-motion start-paused.
const autoplayOptions = {
  delay: 3500,
  disableOnInteraction: false,
  pauseOnMouseEnter: true,
};

export default function initSliders() {

  document.querySelectorAll('.swiper--with-arrows').forEach(el => {
    new Swiper(el, {
      modules: [Navigation, A11y],
      loop: false,
      navigation: {
        nextEl: el.querySelector('.swiper-button-next'),
        prevEl: el.querySelector('.swiper-button-prev'),
      },
      slidesPerView: 1,
      spaceBetween: 25,
      breakpoints: {
        768: {
          slidesPerView: 2
        },
        1024: {
          slidesPerView: 4
        },
      },
    });
  });

  document.querySelectorAll('.swiper--with-dots').forEach(el => {
    const swiper = new Swiper(el, {
      modules: [Pagination, A11y, Autoplay],
      loop: true,
      autoplay: autoplayOptions,
      speed: 500,
      pagination: {
        el: el.querySelector('.swiper-pagination'),
        clickable: true,
      },
      slidesPerView: 1,
    });
    wireAutoplayToggle(swiper, el);
    alignToggleToImage(swiper, el, '.image-content__image img');
  });

  document.querySelectorAll('.project-detail__gallery').forEach(el => {
    // The art-exhibit gallery variant autoplays; the project-detail variant
    // doesn't. rewind (not loop) so autoplay cycles back to the first slide
    // without cloning slides, which would duplicate the lightbox triggers.
    const isArt = el.classList.contains('art-detail__gallery');
    const swiper = new Swiper(el, {
      modules: [Navigation, Pagination, Keyboard, A11y, Autoplay],
      loop: false,
      rewind: isArt,
      autoplay: isArt ? autoplayOptions : false,
      speed: isArt ? 500 : 300,
      slidesPerView: 1,
      spaceBetween: 0,
      keyboard: { enabled: true },
      pagination: {
        el: el.querySelector('.swiper-pagination'),
        clickable: true,
      },
      navigation: {
        nextEl: el.querySelector('.swiper-button-next'),
        prevEl: el.querySelector('.swiper-button-prev'),
      },
    });
    if (isArt) {
      wireAutoplayToggle(swiper, el);
      alignToggleToImage(swiper, el, '.art-lightbox__thumb');
    }
  });

  document.querySelectorAll('.project-carousel__swiper').forEach(el => {
    new Swiper(el, {
      modules: [Navigation, A11y],
      slidesPerView: 2,
      spaceBetween: 16,
      speed: 600,
      navigation: {
        nextEl: el.querySelector('.swiper-button-next'),
        prevEl: el.querySelector('.swiper-button-prev'),
      },
      breakpoints: {
        500: {
          slidesPerView: 3,
        },
        768: {
          slidesPerView: 4,
          spaceBetween: 20,
        },
        1024: {
          slidesPerView: 6,
          spaceBetween: 25,
        },
      },
    });
  });

  document.querySelectorAll('.art-carousel__swiper').forEach(el => {
    new Swiper(el, {
      modules: [Navigation, Keyboard, A11y],
      slidesPerView: 1,
      spaceBetween: 24,
      speed: 600,
      keyboard: { enabled: true, onlyInViewport: true },
      navigation: {
        nextEl: el.querySelector('.swiper-button-next'),
        prevEl: el.querySelector('.swiper-button-prev'),
        disabledClass: 'swiper-button-disabled',
      },
      breakpoints: {
        500: {
          slidesPerView: 2,
        },
        1024: {
          slidesPerView: 3,
        },
      },
    });
  });
}
